import { CognitiveSkill, EmotionalSkill, PracticalSkill, CharacterRole } from '@/types/enums';

export type SkillBranch = 'Cognitive' | 'Emotional' | 'Practical';

export type SkillType = CognitiveSkill | EmotionalSkill | PracticalSkill;

export interface SkillTreeRoleConfig {
  id: CharacterRole;
  branch: SkillBranch;
  prerequisiteSkill: SkillType;
  mpRequirement: number;
  achievementQuest: string;
}

export const SKILL_TREE_ROLES: SkillTreeRoleConfig[] = [
  // COGNITIVE BRANCH
  {
    id: CharacterRole.ADMIN,
    branch: 'Cognitive',
    prerequisiteSkill: CognitiveSkill.ORGANIZATION,
    mpRequirement: 100,
    achievementQuest: "Financial Controller - Close month financials.",
  },
  {
    id: CharacterRole.RESEARCHER,
    branch: 'Cognitive',
    prerequisiteSkill: CognitiveSkill.LOGIC,
    mpRequirement: 100,
    achievementQuest: "Library Contributor - Create a research doc for the library.",
  },
  {
    id: CharacterRole.LIBRARIAN,
    branch: 'Cognitive',
    prerequisiteSkill: CognitiveSkill.MEMORY,
    mpRequirement: 100,
    achievementQuest: "Content Creator - Create or fix an ebook or lesson in the library.",
  },
  {
    id: CharacterRole.STUDENT,
    branch: 'Cognitive',
    prerequisiteSkill: CognitiveSkill.PROBLEM_SOLVING,
    mpRequirement: 100,
    achievementQuest: "Unit Master - Complete an entire unit (a lessons folder).",
  },
  {
    id: CharacterRole.ARCHITECT,
    branch: 'Cognitive',
    prerequisiteSkill: CognitiveSkill.DECISION_MAKING,
    mpRequirement: 100,
    achievementQuest: "System Blueprints - Draft a technical architecture diagram.",
  },
  {
    id: CharacterRole.CYPHER,
    branch: 'Cognitive',
    prerequisiteSkill: CognitiveSkill.VISION,
    mpRequirement: 100,
    achievementQuest: "Web3 Pioneer - Create an NFT collection, smart contract, or other web3 project.",
  },
  {
    id: CharacterRole.GAMER,
    branch: 'Cognitive',
    prerequisiteSkill: CognitiveSkill.DECISION_MAKING,
    mpRequirement: 100,
    achievementQuest: "Game Designer - Design a gamified module or game.",
  },
  {
    id: CharacterRole.STRATEGIST,
    branch: 'Cognitive',
    prerequisiteSkill: CognitiveSkill.PLANNING,
    mpRequirement: 100,
    achievementQuest: "Master Plan - Draft a comprehensive 3-month project roadmap.",
  },
  {
    id: CharacterRole.ANALYST,
    branch: 'Cognitive',
    prerequisiteSkill: CognitiveSkill.PERCEPTION,
    mpRequirement: 100,
    achievementQuest: "Data Miner - Create a report finding patterns in raw data.",
  },
  {
    id: CharacterRole.INNOVATOR,
    branch: 'Cognitive',
    prerequisiteSkill: CognitiveSkill.CREATIVITY,
    mpRequirement: 100,
    achievementQuest: "Paradigm Shift - Propose a completely novel game mechanic or feature.",
  },
  {
    id: CharacterRole.GURU,
    branch: 'Cognitive',
    prerequisiteSkill: CognitiveSkill.WISDOM,
    mpRequirement: 100,
    achievementQuest: "Growth Embodiment - Demonstrate how you applied growth lessons in reality.",
  },

  // EMOTIONAL BRANCH
  {
    id: CharacterRole.ORCHESTRATOR,
    branch: 'Emotional',
    prerequisiteSkill: EmotionalSkill.LEADERSHIP,
    mpRequirement: 100,
    achievementQuest: "Team Synergy - Coordinate a multi-person project milestone.",
  },
  {
    id: CharacterRole.COMMUNICATOR,
    branch: 'Emotional',
    prerequisiteSkill: EmotionalSkill.COMMUNICATION,
    mpRequirement: 100,
    achievementQuest: "The Pitch - Deliver a recorded pitch or presentation.",
  },
  {
    id: CharacterRole.PROMOTER,
    branch: 'Emotional',
    prerequisiteSkill: EmotionalSkill.CHARISMA,
    mpRequirement: 100,
    achievementQuest: "Hype Train - Run a mock marketing campaign for a product.",
  },
  {
    id: CharacterRole.ENTREPRENEUR,
    branch: 'Emotional',
    prerequisiteSkill: EmotionalSkill.INITIATIVE,
    mpRequirement: 100,
    achievementQuest: "0 to 1 - Pitch and launch a new business system or micro-project.",
  },

  // PRACTICAL BRANCH
  {
    id: CharacterRole.DEV,
    branch: 'Practical',
    prerequisiteSkill: PracticalSkill.DEVELOPING,
    mpRequirement: 100,
    achievementQuest: "Bug Hunter - Find and fix an important bug in the digital universe.",
  },
  {
    id: CharacterRole.DESIGNER,
    branch: 'Practical',
    prerequisiteSkill: PracticalSkill.DESIGN_THINKING,
    mpRequirement: 100,
    achievementQuest: "Interface Architect - Design a UI wireframe or mockup.",
  },
  {
    id: CharacterRole.MAKER,
    branch: 'Practical',
    prerequisiteSkill: PracticalSkill.HANDCRAFTING,
    mpRequirement: 100,
    achievementQuest: "The Craftsman - Create a craft or a physical prototype.",
  },
  {
    id: CharacterRole.SELLER,
    branch: 'Practical',
    prerequisiteSkill: PracticalSkill.NEGOTIATION,
    mpRequirement: 100,
    achievementQuest: "The Closer - Successfully complete a mock negotiation or sale.",
  },
  {
    id: CharacterRole.ARTIST,
    branch: 'Practical',
    prerequisiteSkill: PracticalSkill.PAINTING,
    mpRequirement: 100,
    achievementQuest: "First Canvas - Publish an original digital or physical artwork.",
  },
  {
    id: CharacterRole.TEACHER,
    branch: 'Practical',
    prerequisiteSkill: PracticalSkill.TEACHING,
    mpRequirement: 100,
    achievementQuest: "The Mentor - Prepare and teach a class.",
  },
  {
    id: CharacterRole.PM,
    branch: 'Practical',
    prerequisiteSkill: PracticalSkill.PROJECT_MANAGEMENT,
    mpRequirement: 100,
    achievementQuest: "Scrum Master - Successfully run a weekly sprint for a team.",
  },
  {
    id: CharacterRole.MARKETER,
    branch: 'Practical',
    prerequisiteSkill: PracticalSkill.NARRATIVE,
    mpRequirement: 100,
    achievementQuest: "Master Marketer - Write a marketing plan.",
  },
];
